import type { Citation } from "../types";

/**
 * Beyond Sure — RAG citations row.
 *
 * Renders small "source chips" under a grounded assistant answer.
 * When the model is RAG-grounded each chip links to the underlying
 * source (IRDAI regulator, insurer's own brochure, news, internal
 * doc). Chips are color-coded by `kind` so the eye can scan.
 *
 * Phase 2 ships the UI; the data still comes from the mock reply
 * engine. Phase 3 wires `/api/chat/message` to attach real
 * citations from the RAG layer.
 * // TODO: wire to RAG citations
 */

interface CitationsRowProps {
  citations: Citation[];
}

export function CitationsRow({ citations }: CitationsRowProps) {
  if (!citations.length) return null;
  return (
    <div
      data-testid="message-citations"
      className="mt-3 flex items-center gap-2 flex-wrap"
    >
      <span className="bsure-caps text-[9.5px] font-semibold tracking-[0.14em] text-[var(--color-ink-40)]">
        Sources
      </span>
      {citations.map((c, i) => {
        const palette = palettesFor(c.kind);
        return (
          <a
            key={`${c.url}-${i}`}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`citation-${i}`}
            className={[
              "inline-flex items-center gap-1.5",
              "rounded-full px-2.5 py-[3px]",
              "text-[11px] font-medium",
              "transition-colors duration-[var(--duration-fast)]",
            ].join(" ")}
            style={{
              backgroundColor: palette.bg,
              color: palette.ink,
            }}
          >
            <span
              className="inline-block w-1 h-1 rounded-full"
              style={{ backgroundColor: palette.ink, opacity: 0.7 }}
              aria-hidden
            />
            <span className="truncate max-w-[260px]">{c.label}</span>
          </a>
        );
      })}
    </div>
  );
}

function palettesFor(kind?: Citation["kind"]) {
  switch (kind) {
    case "regulator":
      return { bg: "var(--color-brand-soft)", ink: "var(--color-brand-deep)" };
    case "insurer":
      return { bg: "rgb(20 28 24 / 0.06)", ink: "var(--color-ink)" };
    case "news":
      return { bg: "rgb(255 211 0 / 0.18)", ink: "#7a5d00" };
    case "internal":
    default:
      return { bg: "rgb(20 28 24 / 0.04)", ink: "var(--color-ink-60)" };
  }
}

export default CitationsRow;
